import React, { useState, useEffect, useMemo, useContext, useCallback, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { ConnectivityProvider } from './contexts/ConnectivityContext';
import ClockProvider, { ClockContext } from './contexts/ClockContext';
import { CalendarProvider } from './contexts/CalendarContext';
import CalculatorProvider, { CalculatorContext } from './contexts/CalculatorContext';
import DetachableClock from './components/DetachableClock';
import PersistentCalendar from './components/PersistentCalendar';
import PersistentCalculator from './components/PersistentCalculator';
import { getMinutesUntilMidnight } from './utils/dateHelpers';
import {
  toLocalDateString,
  createLocalDate,
  formatLocalDate,
  today
} from './utils/dateUtils';
import OfflineAlert from './components/OfflineAlert';
import { debug } from './utils/debug';
import { setupDetachableComponentsResizeHandler } from './utils/detachableHelpers';
import { BreadcrumbProvider } from './components/Breadcrumbs';
import { Box, CircularProgress } from '@mui/material';

// Components
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import SubscriptionRoute from './components/SubscriptionRoute';
import InactivityWarning from './components/InactivityWarning';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import TwoFactorReminderBanner from './components/TwoFactorReminderBanner';
// qrcode.react (used to render the setup QR code) only matters to the small
// fraction of users actually setting up 2FA, so this is lazy-loaded rather
// than shipped in the main bundle for everyone - same rationale as the
// admin-only pages above.
const TwoFactorSetup = React.lazy(() => import('./components/TwoFactorSetup'));

// Pages needed immediately for unauthenticated visitors (landing/login/etc.)
// stay as regular static imports - these render the instant the app loads
// for anyone not signed in, so there's nothing to gain by deferring them and
// Suspense would only add a fallback flash before showing what's already
// the first thing a logged-out visitor sees.
import Register from './pages/Register';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailVerificationPending from './pages/EmailVerificationPending';
import EmailVerification from './pages/EmailVerification';
import LandingPage from './pages/LandingPage';

// Every one of these pages used to be a static import too, which - unlike
// the admin pages already lazy-loaded below - meant they were bundled and
// parsed as part of the *same* chunk as the routes above. Since a static
// import is hoisted to module load regardless of which of the two <Routes>
// trees below actually renders, every unauthenticated visitor (anyone
// hitting the landing page, /login, /register, ...) was downloading and
// parsing the entire authenticated app - Transactions.js alone is several
// thousand lines, and MonthlyAnalysis/Accounts/Categories each pull in a
// charting library - before they'd even signed in. Lazy-loading these mirrors
// the admin-pages precedent below, just applied to the much larger slice of
// the bundle that's actually gated behind login.
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Transactions = React.lazy(() => import('./pages/Transactions'));
const TransactionsTrending = React.lazy(() => import('./pages/TransactionsTrending'));
const TransactionDetails = React.lazy(() => import('./pages/TransactionDetails'));
const Categories = React.lazy(() => import('./pages/Categories'));
const CategoryDetails = React.lazy(() => import('./pages/CategoryDetails'));
const Accounts = React.lazy(() => import('./pages/Accounts'));
const AccountDetails = React.lazy(() => import('./pages/AccountDetails'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Sessions = React.lazy(() => import('./pages/Sessions'));
const BudgetsPage = React.lazy(() => import('./pages/BudgetsPage'));
const RecurringTransactions = React.lazy(() => import('./pages/RecurringTransactions'));
const TagsPage = React.lazy(() => import('./pages/TagsPage'));
const MerchantsPage = React.lazy(() => import('./pages/MerchantsPage'));
const MerchantDetails = React.lazy(() => import('./pages/MerchantDetails'));
const CurrenciesPage = React.lazy(() => import('./pages/CurrenciesPage'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));
const MonthlyAnalysis = React.lazy(() => import('./pages/MonthlyAnalysis'));
const Goals = React.lazy(() => import('./pages/Goals'));
// Admin-only pages, gated behind AdminRoute below - every other user never
// visits these, so they don't need to be in the main bundle every user
// downloads and parses on load. Lazy-loaded so they're fetched (and their
// own dependencies pulled in) only when an admin actually navigates here.
const Users = React.lazy(() => import('./pages/Users'));
const AdminSettings = React.lazy(() => import('./pages/AdminSettings'));
const UserDetails = React.lazy(() => import('./pages/UserDetails'));

// Static (no props/state), so it's safe to hoist out of the component -
// otherwise every route below would need its own inline JSX literal
// recreated on every render.
const pageFallback = (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <CircularProgress />
  </Box>
);

function getSeasonalThemeForDate(date = today()) {
  const month = date.getMonth();

  if (month >= 2 && month <= 4) {
    return 'spring';
  }

  if (month >= 5 && month <= 7) {
    return 'summer';
  }

  if (month >= 8 && month <= 10) {
    return 'autumn';
  }

  return 'winter';
}

// Create Theme Context with default values
export const ThemeContext = React.createContext({
  mode: 'light',
  themePreference: 'auto',
  setThemePreference: () => {},
  toggleTheme: () => {},
  sidebarConfig: {
    hideable: false
  },
  updateSidebarConfig: () => {},
  weatherThemeEnabled: true,
  toggleWeatherTheme: () => {},
  weatherThemeMode: 'manual',
  updateWeatherThemeMode: () => {},
  selectedWeatherTheme: getSeasonalThemeForDate(),
  updateSelectedWeatherTheme: () => {},
  weatherCondition: getSeasonalThemeForDate(),
  solidDetachableComponents: false,  // New setting for solid vs transparent detachable components
  toggleDetachableComponentsTransparency: () => {} // Toggle function
});

// Create the CacheContext to make cache clearing available globally
export const CacheContext = React.createContext({
  clearCache: () => {}
});

// clearCache doesn't close over any component state - it only touches the
// module-level api.js cache singleton - so this value is genuinely stable
// forever and can be a plain module constant rather than rebuilt (as a
// fresh object + fresh function) on every render of the two components
// below that provide it, which would otherwise re-render every
// useContext(CacheContext) consumer on every unrelated render.
const cacheContextValue = {
  clearCache: () => {
    // Import and call the cache clearAll function from api.js
    // This is a dynamic import to avoid circular dependencies
    import('./services/api').then(apiModule => {
      if (apiModule.cache && typeof apiModule.cache.clearAll === 'function') {
        apiModule.cache.clearAll();
        debug('session', 'Cache cleared globally');
      }
    });
  }
};

// Create a new component to wrap the persistent clock
const PersistentClockWrapper = () => {
  const { showClock, isClockDetached, handleClockClose } = useContext(ClockContext);
  
  if (!isClockDetached || !showClock) return null;
  
  return <DetachableClock onClose={handleClockClose} />;
};

// Persistent Calculator wrapper component
function PersistentCalculatorWrapper() {
  const { showCalculator } = useContext(CalculatorContext);
  return showCalculator ? <PersistentCalculator /> : null;
}

function AppContent() {
  const { currentUser, loading, updateInactivityTimeout } = useAuth();
  
  // Get theme settings from localStorage or use defaults
  const seasonalDefaultTheme = getSeasonalThemeForDate();
  const storedMode = localStorage.getItem('themeMode') || 'light';
  const normalizedStoredMode = ['light', 'dark', 'auto'].includes(storedMode)
    ? storedMode
    : 'auto';
  const getAutoThemeMode = useCallback(() => {
    const hour = new Date().getHours();
    return hour >= 6 && hour < 18 ? 'light' : 'dark';
  }, []);

  const storedWeatherThemeEnabled = localStorage.getItem('weatherThemeEnabled') === 'true';
  const storedWeatherThemeMode = localStorage.getItem('weatherThemeMode') || 'auto';
  const storedSelectedWeatherTheme = localStorage.getItem('selectedWeatherTheme') || seasonalDefaultTheme;
  const rawWeatherThemeEnabled = localStorage.getItem('weatherThemeEnabled');
  if (rawWeatherThemeEnabled === null) {
    localStorage.setItem('weatherThemeEnabled', 'true');
  }
  const rawWeatherThemeMode = localStorage.getItem('weatherThemeMode');
  if (rawWeatherThemeMode === null) {
    localStorage.setItem('weatherThemeMode', 'manual');
  }
  const rawSelectedWeatherTheme = localStorage.getItem('selectedWeatherTheme');
  if (rawSelectedWeatherTheme === null) {
    localStorage.setItem('selectedWeatherTheme', seasonalDefaultTheme);
  }
  const rawSolidDetachableComponents = localStorage.getItem('solidDetachableComponents');
  if (rawSolidDetachableComponents === null) {
    localStorage.setItem('solidDetachableComponents', 'false');
  }
  const storedSolidDetachableComponents = rawSolidDetachableComponents === 'true'; // default to false
  
  // Initialize cache setting to false if not set
  if (localStorage.getItem('cacheEnabled') === null) {
    localStorage.setItem('cacheEnabled', 'false');
  }
  
  // State for saved theme preference (light/dark/auto)
  const [themePreference, setThemePreference] = useState(normalizedStoredMode);

  // State for theme mode (light/dark)
  const [effectiveMode, setEffectiveMode] = useState(() =>
    normalizedStoredMode === 'auto' ? getAutoThemeMode() : normalizedStoredMode
  );
  
  // State for weather theme toggle
  const [weatherThemeEnabled, setWeatherThemeEnabled] = useState(storedWeatherThemeEnabled);
  
  // State for weather theme mode (auto/manual/cycle)
  const [weatherThemeMode, setWeatherThemeMode] = useState(storedWeatherThemeMode);

  // State for selected weather theme when in manual mode
  const [selectedWeatherTheme, setSelectedWeatherTheme] = useState(storedSelectedWeatherTheme);

  // State for solid/transparent detachable components
  const [solidDetachableComponents, setSolidDetachableComponents] = useState(storedSolidDetachableComponents);
  
  // Default timeout duration (30 minutes) and idle threshold (25 minutes)
  const [inactivityTimeout, setInactivityTimeout] = useState(30);
  const [idleThreshold, setIdleThreshold] = useState(25);
  
  // Load weather condition from localStorage (if available)
  const rawWeatherCondition = localStorage.getItem('weatherCondition');
  if (rawWeatherCondition === null && storedWeatherThemeMode === 'manual') {
    localStorage.setItem('weatherCondition', storedSelectedWeatherTheme);
  }
  const storedWeatherCondition = rawWeatherCondition || (storedWeatherThemeMode === 'manual' ? storedSelectedWeatherTheme : 'clear');
  const [weatherCondition, setWeatherCondition] = useState(
    storedWeatherCondition
  );
  
  // Load sidebar configuration from localStorage or default to hideable
  const [sidebarConfig, setSidebarConfig] = useState({
    hideable: localStorage.getItem('sidebarHideable') !== 'false'
  });

  // 2FA setup dialog state
  const [setup2FAOpen, setSetup2FAOpen] = useState(false);
  // Sticky once true: mounts the lazy TwoFactorSetup dialog the first time
  // it's actually needed, then leaves it mounted (with open=false) so its
  // own Dialog can still run its normal closing transition, rather than
  // conditionally rendering on setup2FAOpen directly - which would unmount
  // it (and cut off the closing animation) the instant the dialog closes.
  const [has2FASetupMounted, setHas2FASetupMounted] = useState(false);

  // 2FA setup callback function
  const handleSetup2FA = () => {
    setSetup2FAOpen(true);
    setHas2FASetupMounted(true);
  };

  // 2FA setup success callback
  const handle2FASetupSuccess = () => {
    setSetup2FAOpen(false);
    // The TwoFactorReminderBanner will automatically hide when 2FA is enabled
  };

  // Watch for changes in weather condition and theme preference
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'weatherCondition') {
        setWeatherCondition(e.newValue || 'clear');
      } else if (e.key === 'weatherThemeEnabled') {
        setWeatherThemeEnabled(e.newValue !== 'false');
      } else if (e.key === 'weatherThemeMode') {
        setWeatherThemeMode(e.newValue || 'manual');
      } else if (e.key === 'selectedWeatherTheme') {
        setSelectedWeatherTheme(e.newValue || getSeasonalThemeForDate());
      } else if (e.key === 'themeMode') {
        const newPreference = e.newValue || 'auto';
        setThemePreference(['light', 'dark', 'auto'].includes(newPreference) ? newPreference : 'auto');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Check for updates when component mounts
    const storedCondition = localStorage.getItem('weatherCondition');
    if (storedCondition) {
      setWeatherCondition(storedCondition);
    }
    
    const storedThemePreference = localStorage.getItem('weatherThemeEnabled');
    setWeatherThemeEnabled(storedThemePreference !== 'false');

    const storedThemeMode = localStorage.getItem('weatherThemeMode');
    if (storedThemeMode) {
      setWeatherThemeMode(storedThemeMode);
    }

    const storedSelectedTheme = localStorage.getItem('selectedWeatherTheme');
    if (storedSelectedTheme) {
      setSelectedWeatherTheme(storedSelectedTheme);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Daily theme cycling - check and update themes at midnight
  useEffect(() => {
    // Only run this effect if one of the cycling modes is active
    if (!['cycle_weather', 'cycle_seasons', 'cycle_all'].includes(weatherThemeMode) || !weatherThemeEnabled) {
      return;
    }

    // Define theme arrays for each cycle mode
    const weatherThemes = ['clear', 'clouds', 'rain', 'snow', 'thunderstorm'];
    const seasonThemes = ['spring', 'summer', 'autumn', 'winter'];
    const allThemes = [...weatherThemes, ...seasonThemes];
    
    // Get current active theme list based on mode
    const activeThemes = weatherThemeMode === 'cycle_weather' ? weatherThemes :
                        weatherThemeMode === 'cycle_seasons' ? seasonThemes : 
                        allThemes;
    
    // Get today's date as a number to use as an index (using day of month for simplicity)
    const todayDate = today().getDate();
    const themeIndex = (todayDate - 1) % activeThemes.length;
    
    // Select today's theme
    const todayTheme = activeThemes[themeIndex];
    
    // Update the theme
    setSelectedWeatherTheme(todayTheme);
    setWeatherCondition(todayTheme);
    localStorage.setItem('selectedWeatherTheme', todayTheme);
    localStorage.setItem('weatherCondition', todayTheme);
    debug('theme', `Daily theme cycle: Setting today's theme to ${todayTheme}`);
    
    // Schedule next update at midnight
    const minutesUntilMidnight = getMinutesUntilMidnight();
    const millisecondsUntilMidnight = minutesUntilMidnight * 60 * 1000;
    
    debug('theme', `Scheduling next theme change in ${minutesUntilMidnight} minutes`);
    
    const midnightTimer = setTimeout(() => {
      // Force a page reload at midnight to apply the new theme
      window.location.reload();
    }, millisecondsUntilMidnight);
    
    return () => {
      clearTimeout(midnightTimer);
    };
  }, [weatherThemeMode, weatherThemeEnabled]);

  // Save theme preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('themeMode', themePreference);
  }, [themePreference]);

  // Update the effective mode when preference changes
  useEffect(() => {
    if (themePreference === 'auto') {
      const updateModeForAutoPreference = () => {
        const autoMode = getAutoThemeMode();
        setEffectiveMode((prevMode) => (prevMode === autoMode ? prevMode : autoMode));
      };

      updateModeForAutoPreference();

      const interval = setInterval(updateModeForAutoPreference, 60 * 1000);

      return () => {
        clearInterval(interval);
      };
    }

    setEffectiveMode(themePreference);
  }, [themePreference, getAutoThemeMode]);
  
  // Add effect to reset weather condition if in auto mode on startup
  useEffect(() => {
    // If we're in auto mode and weather theme is enabled, make sure we clear any stored manual condition
    if (weatherThemeMode === 'auto' && weatherThemeEnabled) {
      // Don't clear if we have a real weather condition already
      const storedCondition = localStorage.getItem('weatherCondition');
      const lastWeatherFetch = localStorage.getItem('lastWeatherFetch');
      
      // Only clear if the condition is more than 2 hours old or matches a saved manual theme
      if (storedCondition === selectedWeatherTheme || 
          !lastWeatherFetch || 
          (today().getTime() - new Date(lastWeatherFetch).getTime() > 2 * 60 * 60 * 1000)) {
        // Clear the stored condition to force fetching fresh weather
        localStorage.removeItem('weatherCondition');
      }
    }
  }, [weatherThemeMode, weatherThemeEnabled, selectedWeatherTheme]);
  
  // Save sidebar config to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarHideable', sidebarConfig.hideable.toString());
  }, [sidebarConfig.hideable]);

  // Toggle weather theme preference
  const toggleWeatherTheme = useCallback(() => {
    const newValue = !weatherThemeEnabled;
    setWeatherThemeEnabled(newValue);
    localStorage.setItem('weatherThemeEnabled', newValue.toString());

    // If enabling weather theme in auto mode, clear any stored manual condition
    if (newValue && weatherThemeMode === 'auto') {
      localStorage.removeItem('weatherCondition');
      // Force a reload to fetch fresh weather data
      window.location.reload();
    }
  }, [weatherThemeEnabled, weatherThemeMode]);

  // Add direct setters for weather theme settings
  const updateWeatherThemeMode = useCallback((newMode) => {
    debug('theme', 'Setting weather theme mode to:', newMode);
    setWeatherThemeMode(newMode);
    localStorage.setItem('weatherThemeMode', newMode);
    
    // Handle different modes
    if (newMode === 'manual' && weatherThemeEnabled) {
      // Manual mode - set the weather condition to the selected theme
      localStorage.setItem('weatherCondition', selectedWeatherTheme);
    }
    else if (newMode === 'auto' && weatherThemeEnabled) {
      // Auto mode - clear any stored manual weather condition
      localStorage.removeItem('weatherCondition');
    }
    else if (['cycle_weather', 'cycle_seasons', 'cycle_all'].includes(newMode) && weatherThemeEnabled) {
      // Cycling modes - immediately calculate and set today's theme
      
      // Define theme arrays for each cycle mode
      const weatherThemes = ['clear', 'clouds', 'rain', 'snow', 'thunderstorm'];
      const seasonThemes = ['spring', 'summer', 'autumn', 'winter'];
      const allThemes = [...weatherThemes, ...seasonThemes];
      
      // Get current active theme list based on mode
      const activeThemes = newMode === 'cycle_weather' ? weatherThemes :
                          newMode === 'cycle_seasons' ? seasonThemes : 
                          allThemes;
      
      // Get today's date as a number to use as an index
      const todayDate = today().getDate();
      const themeIndex = (todayDate - 1) % activeThemes.length;
      
      // Select today's theme
      const todayTheme = activeThemes[themeIndex];
      
      // Update the theme
      setSelectedWeatherTheme(todayTheme);
      setWeatherCondition(todayTheme);
      localStorage.setItem('selectedWeatherTheme', todayTheme);
      localStorage.setItem('weatherCondition', todayTheme);
      debug('theme', `Daily theme cycle: Setting today's theme to ${todayTheme}`);
    }
    
    // Manually dispatch storage event to notify other tabs/components
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'weatherThemeMode',
      newValue: newMode,
      storageArea: localStorage
    }));
  }, [weatherThemeEnabled, selectedWeatherTheme]);

  const updateSelectedWeatherTheme = useCallback((newTheme) => {
    debug('theme', 'Setting selected weather theme to:', newTheme);
    setSelectedWeatherTheme(newTheme);
    localStorage.setItem('selectedWeatherTheme', newTheme);

    // If in manual mode, also update the weather condition
    if (weatherThemeMode === 'manual' && weatherThemeEnabled) {
      setWeatherCondition(newTheme);
      localStorage.setItem('weatherCondition', newTheme);
    }

    // Manually dispatch storage event to notify other tabs/components
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'selectedWeatherTheme',
      newValue: newTheme,
      storageArea: localStorage
    }));
  }, [weatherThemeMode, weatherThemeEnabled]);

  const updateThemePreference = useCallback((newPreference) => {
    if (!['light', 'dark', 'auto'].includes(newPreference)) {
      return;
    }

    setThemePreference(newPreference);
  }, [setThemePreference]);

  // Theme toggle function
  const toggleTheme = useCallback(() => {
    if (themePreference === 'auto') {
      updateThemePreference(effectiveMode === 'light' ? 'dark' : 'light');
      return;
    }

    updateThemePreference(themePreference === 'light' ? 'dark' : 'light');
  }, [themePreference, effectiveMode, updateThemePreference]);

  // Function to update sidebar config
  const updateSidebarConfig = useCallback((newConfig) => {
    setSidebarConfig(prev => ({
      ...prev,
      ...newConfig
    }));
  }, []);

  // Get weather-based theme colors
  const getWeatherTheme = (condition, isDark) => {
    switch (condition) {
      case 'clear':
        return {
          primary: {
            main: isDark ? '#ff9800' : '#ffc107', // Orange/amber for clear/sunny
            light: isDark ? '#ffb74d' : '#ffca28',
            dark: isDark ? '#f57c00' : '#ffb300'
          },
          secondary: {
            main: isDark ? '#ff7043' : '#ff9800', // deep orange in dark, orange in light
            light: isDark ? '#ff8a65' : '#ffb74d',
            dark: isDark ? '#f4511e' : '#f57c00'
          },
          background: {
            default: isDark ? '#121212' : '#fffaf0', // Dark or light warm tone
            paper: isDark ? '#1e1e1e' : '#ffffff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        };
      
      case 'spring':
        return {
          primary: {
            main: isDark ? '#66bb6a' : '#81c784', // Green for spring growth
            light: isDark ? '#81c784' : '#a5d6a7',
            dark: isDark ? '#43a047' : '#66bb6a'
          },
          secondary: {
            main: isDark ? '#ec407a' : '#f48fb1', // Pink for spring flowers
            light: isDark ? '#f06292' : '#f8bbd0',
            dark: isDark ? '#d81b60' : '#ec407a'
          },
          background: {
            default: isDark ? '#0d1f0f' : '#f1f8e9', // Soft green background
            paper: isDark ? '#1a2e1c' : '#ffffff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.07)',
        };
      
      case 'summer':
        return {
          primary: {
            main: isDark ? '#26c6da' : '#00bcd4', // Cyan/teal for summer water
            light: isDark ? '#4dd0e1' : '#4dd0e1',
            dark: isDark ? '#0097a7' : '#00acc1'
          },
          secondary: {
            main: isDark ? '#ffab00' : '#ffd54f', // Bright yellow for summer sun
            light: isDark ? '#ffc400' : '#ffe082',
            dark: isDark ? '#ff8f00' : '#ffb300'
          },
          background: {
            default: isDark ? '#0d2226' : '#e0f7fa', // Light blue tone for summer sky
            paper: isDark ? '#102027' : '#ffffff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.13)' : 'rgba(0, 0, 0, 0.06)',
        };
      
      case 'autumn':
        return {
          primary: {
            main: isDark ? '#ff7043' : '#ff5722', // Deep orange for autumn leaves
            light: isDark ? '#ff8a65' : '#ff8a65',
            dark: isDark ? '#e64a19' : '#e64a19'
          },
          secondary: {
            main: isDark ? '#8d6e63' : '#a1887f', // Brown for autumn earth
            light: isDark ? '#a1887f' : '#bcaaa4',
            dark: isDark ? '#6d4c41' : '#8d6e63'
          },
          background: {
            default: isDark ? '#211709' : '#fbe9e7', // Warm tone for autumn
            paper: isDark ? '#321911' : '#ffffff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.13)' : 'rgba(0, 0, 0, 0.08)',
        };
      
      case 'winter':
        return {
          primary: {
            main: isDark ? '#90caf9' : '#2196f3', // Blue for winter ice
            light: isDark ? '#bbdefb' : '#64b5f6',
            dark: isDark ? '#42a5f5' : '#1976d2'
          },
          secondary: {
            main: isDark ? '#b39ddb' : '#7e57c2', // Purple for winter twilight
            light: isDark ? '#d1c4e9' : '#9575cd',
            dark: isDark ? '#7986cb' : '#5e35b1'
          },
          background: {
            default: isDark ? '#10141c' : '#e8eaf6', // Cool blue tone for winter
            paper: isDark ? '#1a223a' : '#ffffff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.05)',
        };
      
      case 'clouds':
        return {
          primary: {
            main: isDark ? '#455a64' : '#607d8b', // blue grey
            light: isDark ? '#546e7a' : '#78909c',
            dark: isDark ? '#37474f' : '#455a64'
          },
          secondary: {
            main: isDark ? '#78909c' : '#90a4ae', // lighter blue grey
            light: isDark ? '#90a4ae' : '#b0bec5',
            dark: isDark ? '#546e7a' : '#78909c'
          },
          background: {
            default: isDark ? '#121212' : '#f5f5f5',
            paper: isDark ? '#1e1e1e' : '#ffffff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.09)' : 'rgba(0, 0, 0, 0.07)',
        };
      
      case 'rain':
      case 'drizzle':
        return {
          primary: {
            main: isDark ? '#00796b' : '#26a69a', // Teal for rain instead of blue
            light: isDark ? '#00897b' : '#4db6ac',
            dark: isDark ? '#00695c' : '#00897b'
          },
          secondary: {
            main: isDark ? '#0097a7' : '#00acc1', // teal-ish
            light: isDark ? '#00acc1' : '#26c6da',
            dark: isDark ? '#00838f' : '#00838f'
          },
          background: {
            default: isDark ? '#0a1921' : '#e0f2f1',
            paper: isDark ? '#102a32' : '#ffffff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.11)' : 'rgba(0, 0, 0, 0.06)',
        };
      
      case 'snow':
        return {
          primary: {
            main: isDark ? '#cfd8dc' : '#eceff1', // Light blue-gray for snow
            light: isDark ? '#eceff1' : '#fafafa',
            dark: isDark ? '#b0bec5' : '#cfd8dc'
          },
          secondary: {
            main: isDark ? '#b39ddb' : '#ce93d8', // light purple
            light: isDark ? '#d1c4e9' : '#e1bee7',
            dark: isDark ? '#9575cd' : '#ba68c8'
          },
          background: {
            default: isDark ? '#1a1a24' : '#f7f9fa',
            paper: isDark ? '#2b2b3d' : '#ffffff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.13)' : 'rgba(0, 0, 0, 0.05)',
        };
      
      case 'thunderstorm':
        return {
          primary: {
            main: isDark ? '#673ab7' : '#9c27b0', // Purple for thunderstorm
            light: isDark ? '#7e57c2' : '#ba68c8',
            dark: isDark ? '#512da8' : '#7b1fa2'
          },
          secondary: {
            main: isDark ? '#f9a825' : '#ffc107', // amber/yellow for lightning
            light: isDark ? '#fbc02d' : '#ffca28',
            dark: isDark ? '#f57f17' : '#ffb300'
          },
          background: {
            default: isDark ? '#0a0514' : '#f3e5f5',
            paper: isDark ? '#1a1025' : '#ffffff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.09)',
        };
      
      default:
        return {
          primary: {
            main: '#1976d2', // Default blue
          },
          secondary: {
            main: '#dc004e', // Default pink
          },
          background: {
            default: isDark ? '#121212' : '#f5f5f5',
            paper: isDark ? '#1e1e1e' : '#fff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.07)',
        };
    }
  };

  // Create theme based on current mode and weather (if enabled)
  const theme = useMemo(() => {
    const isDark = effectiveMode === 'dark';
    
    // Determine which weather condition to use based on the theme mode
    const effectiveWeatherCondition = weatherThemeEnabled
      ? (weatherThemeMode === 'manual' || weatherThemeMode === 'cycle_weather' || 
         weatherThemeMode === 'cycle_seasons' || weatherThemeMode === 'cycle_all' 
         ? selectedWeatherTheme : weatherCondition)
      : null;
    
    // Only use weather-based theme if enabled, otherwise use default theme
    const weatherColors = weatherThemeEnabled 
      ? getWeatherTheme(effectiveWeatherCondition, isDark)
      : {
          primary: {
            main: '#1976d2', // Default blue
          },
          secondary: {
            main: '#dc004e', // Default pink
          },
          background: {
            default: isDark ? '#121212' : '#f5f5f5',
            paper: isDark ? '#1e1e1e' : '#fff',
          },
          divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.07)',
        };
    
    return createTheme({
      palette: {
        mode: effectiveMode,
        ...weatherColors,
      },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              transition: 'background-color 0.5s ease-in-out',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              transition: 'background-color 0.5s ease-in-out, box-shadow 0.3s ease',
            },
          },
        },
        // Add specific styling for drawer and AppBar when clear theme is active
        ...(effectiveWeatherCondition === 'clear' && {
          MuiDrawer: {
            styleOverrides: {
              paper: {
                background: isDark 
                  ? 'linear-gradient(145deg, rgba(230, 81, 0, 0.2) 0%, rgba(255, 152, 0, 0.25) 100%)' 
                  : 'linear-gradient(145deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 193, 7, 0.2) 100%)',
                color: isDark ? '#ffffff' : '#212121',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                background: isDark
                  ? 'linear-gradient(to right, rgba(230, 81, 0, 0.4), rgba(255, 152, 0, 0.5))'
                  : 'linear-gradient(to right, rgba(255, 152, 0, 0.3), rgba(255, 193, 7, 0.4))',
              },
            },
          },
          // Add styling for Paper components in Settings page
          MuiAccordion: {
            styleOverrides: {
              root: {
                background: isDark 
                  ? 'linear-gradient(145deg, rgba(230, 81, 0, 0.1) 0%, rgba(255, 152, 0, 0.15) 100%)' 
                  : 'linear-gradient(145deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 193, 7, 0.1) 100%)',
              },
            },
          },
        }),
      },
    });
  }, [effectiveMode, weatherCondition, weatherThemeEnabled, weatherThemeMode, selectedWeatherTheme]);

  const toggleDetachableComponentsTransparency = useCallback(() => {
    const newValue = !solidDetachableComponents;
    setSolidDetachableComponents(newValue);
    localStorage.setItem('solidDetachableComponents', newValue.toString());
  }, [solidDetachableComponents]);

  // Inside AppContent component, add ThemeContext Provider with all values.
  // Memoized - this was a fresh object literal on every AppContent render,
  // and ThemeContext wraps the entire authenticated app (consumed via
  // useContext(ThemeContext) in 20+ page/component files: Navigation,
  // Dashboard, Sessions, TagsPage, RecurringTransactions, Goals, ...). Any
  // unrelated state change in this component - the periodic weather-
  // condition update, a sidebar toggle, the 2FA dialog opening - hands out
  // a new reference and re-renders every one of those consumer trees, even
  // when the specific value they read didn't change. Same bug class already
  // fixed for AuthContext/ConnectivityContext, left here in App.js itself.
  // The functions above are now useCallback'd so this memoization actually
  // holds across renders that don't change their own dependencies, instead
  // of recomputing every render anyway.
  const themeContextValue = useMemo(() => ({
    mode: effectiveMode,
    themePreference,
    setThemePreference: updateThemePreference,
    toggleTheme,
    sidebarConfig,
    updateSidebarConfig,
    weatherThemeEnabled,
    toggleWeatherTheme,
    weatherThemeMode,
    updateWeatherThemeMode,
    selectedWeatherTheme,
    updateSelectedWeatherTheme,
    weatherCondition: weatherThemeEnabled
      ? (weatherThemeMode === 'manual' || weatherThemeMode === 'cycle_weather' ||
         weatherThemeMode === 'cycle_seasons' || weatherThemeMode === 'cycle_all'
         ? selectedWeatherTheme : weatherCondition)
      : weatherCondition,
    solidDetachableComponents,
    toggleDetachableComponentsTransparency
  }), [
    effectiveMode,
    themePreference,
    updateThemePreference,
    toggleTheme,
    sidebarConfig,
    updateSidebarConfig,
    weatherThemeEnabled,
    toggleWeatherTheme,
    weatherThemeMode,
    updateWeatherThemeMode,
    selectedWeatherTheme,
    updateSelectedWeatherTheme,
    weatherCondition,
    solidDetachableComponents,
    toggleDetachableComponentsTransparency
  ]);

  useEffect(() => {
    // Check if user had "keep signed in" enabled and restore that setting
    if (currentUser) {
      const wasKeepSignedInEnabled = localStorage.getItem('keepSignedIn') === 'true';
      const storedExpiryTime = localStorage.getItem('signInExpiryTime');
      
      if (wasKeepSignedInEnabled) {
        if (storedExpiryTime) {
          const expiryTime = new Date(parseInt(storedExpiryTime, 10));
          const now = new Date();
          
          // If expiry time is in the future
          if (expiryTime > now) {
            // Calculate remaining minutes until midnight
            const minutesRemaining = Math.ceil((expiryTime - now) / (1000 * 60));
            
            // Re-apply the timeout to expire at midnight
            debug('session', `Restoring session to expire at midnight (${minutesRemaining} minutes remaining)`);
            updateInactivityTimeout(minutesRemaining);
            setInactivityTimeout(minutesRemaining);
            setIdleThreshold(Math.max(minutesRemaining - 5, 1)); // Set idle threshold 5 minutes before timeout
          } else {
            // Past midnight, set new expiry time for today
            debug('session', 'Past midnight, setting new keep signed in session for today');
            const midnight = new Date();
            midnight.setHours(24, 0, 0, 0);
            const minutesUntilMidnight = Math.ceil((midnight - now) / (1000 * 60));
            
            updateInactivityTimeout(minutesUntilMidnight);
            setInactivityTimeout(minutesUntilMidnight);
            setIdleThreshold(Math.max(minutesUntilMidnight - 5, 1));
            localStorage.setItem('signInExpiryTime', midnight.getTime().toString());
          }
        } else {
          // No stored expiry time but keepSignedIn is enabled, set new expiry for today
          debug('session', 'Keep signed in enabled but no expiry time, setting new session for today');
          const now = new Date();
          const midnight = new Date();
          midnight.setHours(24, 0, 0, 0);
          const minutesUntilMidnight = Math.ceil((midnight - now) / (1000 * 60));
          
          updateInactivityTimeout(minutesUntilMidnight);
          setInactivityTimeout(minutesUntilMidnight);
          setIdleThreshold(Math.max(minutesUntilMidnight - 5, 1));
          localStorage.setItem('signInExpiryTime', midnight.getTime().toString());
        }
      }
    }
  }, [currentUser, updateInactivityTimeout]);

  // Initialize detachable components resize handler
  useEffect(() => {
    // Set up the resize handler when the app loads
    const cleanupResizeHandler = setupDetachableComponentsResizeHandler();
    
    // Clean up the handler when the component unmounts
    return () => {
      if (cleanupResizeHandler) {
        cleanupResizeHandler();
      }
    };
  }, []);

  // Show loading screen while authentication is being checked
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: 'background.default'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <ThemeProvider theme={theme}>
        <ConnectivityProvider>
          <ClockProvider>
            <CalendarProvider>
              <CalculatorProvider>
                <CacheContext.Provider value={cacheContextValue}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <CssBaseline />
                    <BreadcrumbProvider>
                      <Router>
                        {currentUser ? (
                          <Navigation>
                            {/* Persistent detachable clock */}
                            <PersistentClockWrapper />
                            
                            {/* Persistent detachable calendar */}
                            <PersistentCalendar />
                            
                            {/* Persistent detachable calculator */}
                            <PersistentCalculatorWrapper />
                            
                            {/* Offline Alert */}
                            <OfflineAlert />
                            
                            {/* Email Verification Banner - Show for unverified authenticated users */}
                            <EmailVerificationBanner />
                            
                            {/* Two-Factor Authentication Reminder Banner */}
                            <TwoFactorReminderBanner onSetup2FA={handleSetup2FA} />
                            
                            {/* Two-Factor Authentication Setup Dialog */}
                            {has2FASetupMounted && (
                              <Suspense fallback={null}>
                                <TwoFactorSetup
                                  open={setup2FAOpen}
                                  onClose={() => setSetup2FAOpen(false)}
                                  onSuccess={handle2FASetupSuccess}
                                />
                              </Suspense>
                            )}

                                                        {/* Every route below (aside from the
                                admin ones, each already individually wrapped) now renders a
                                React.lazy() page component - a single Suspense boundary around
                                the whole tree covers all of them, since React Router only ever
                                renders the one matched route at a time. */}
                            <Suspense fallback={pageFallback}>
                            <Routes>
                              {/* Protected routes */}
                            <Route path="/dashboard" element={
                              <ProtectedRoute>
                                <Dashboard />
                              </ProtectedRoute>
                            } />
                            <Route path="/transactions" element={
                              <ProtectedRoute>
                                <Transactions />
                              </ProtectedRoute>
                            } />
                            <Route path="/transactions/trending" element={
                              <ProtectedRoute>
                                <TransactionsTrending />
                              </ProtectedRoute>
                            } />
                            <Route path="/transactions/:transactionId/details" element={
                              <ProtectedRoute>
                                <TransactionDetails />
                              </ProtectedRoute>
                            } />
                            <Route path="/categories" element={
                              <ProtectedRoute>
                                <Categories />
                              </ProtectedRoute>
                            } />
                            <Route path="/categories/:id" element={
                              <ProtectedRoute>
                                <CategoryDetails />
                              </ProtectedRoute>
                            } />
                            <Route path="/accounts" element={
                              <ProtectedRoute>
                                <Accounts />
                              </ProtectedRoute>
                            } />
                            <Route path="/accounts/:accountId" element={
                              <ProtectedRoute>
                                <AccountDetails />
                              </ProtectedRoute>
                            } />
                            <Route path="/settings" element={
                              <ProtectedRoute>
                                <Settings />
                              </ProtectedRoute>
                            } />
                            <Route path="/sessions" element={
                              <ProtectedRoute>
                                <Sessions />
                              </ProtectedRoute>
                            } />
                            <Route path="/budgets" element={
                              <ProtectedRoute>
                                <BudgetsPage />
                              </ProtectedRoute>
                            } />
                            <Route path="/recurring-transactions" element={
                              <ProtectedRoute>
                                <RecurringTransactions />
                              </ProtectedRoute>
                            } />
                            <Route path="/tags" element={
                              <ProtectedRoute>
                                <TagsPage />
                              </ProtectedRoute>
                            } />
                            <Route path="/merchants/:merchantId" element={
                              <ProtectedRoute>
                                <MerchantDetails />
                              </ProtectedRoute>
                            } />
                            <Route path="/merchants" element={
                              <ProtectedRoute>
                                <MerchantsPage />
                              </ProtectedRoute>
                            } />
                            <Route path="/goals" element={
                              <ProtectedRoute>
                                <Goals />
                              </ProtectedRoute>
                            } />
                            <Route path="/currencies" element={
                              <ProtectedRoute>
                                <CurrenciesPage />
                              </ProtectedRoute>
                            } />
                            <Route path="/monthly-analysis" element={
                              <ProtectedRoute>
                                <MonthlyAnalysis />
                              </ProtectedRoute>
                            } />
                            <Route path="/subscription" element={<SubscriptionPage />} />
                            <Route path="/admin/users" element={
                              <ProtectedRoute>
                                <AdminRoute>
                                  <Suspense fallback={pageFallback}>
                                    <Users />
                                  </Suspense>
                                </AdminRoute>
                              </ProtectedRoute>
                            } />
                            <Route path="/admin/users/:userId" element={
                              <ProtectedRoute>
                                <AdminRoute>
                                  <Suspense fallback={pageFallback}>
                                    <UserDetails />
                                  </Suspense>
                                </AdminRoute>
                              </ProtectedRoute>
                            } />
                            <Route path="/admin/settings" element={
                              <ProtectedRoute>
                                <AdminRoute>
                                  <Suspense fallback={pageFallback}>
                                    <AdminSettings />
                                  </Suspense>
                                </AdminRoute>
                              </ProtectedRoute>
                            } />
                            
                            {/* Fallback route */}
                            <Route path="*" element={<Navigate to="/dashboard" />} />
                          </Routes>
                            </Suspense>
                          </Navigation>
                        ) : (
                          <Routes>
                            {/* Public routes for unauthenticated users */}
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password/:token" element={<ResetPassword />} />
                            <Route path="/verify-email-pending" element={<EmailVerificationPending />} />
                            <Route path="/verify-email" element={<EmailVerification />} />
                            
                            {/* Fallback route for unauthenticated users */}
                            <Route path="*" element={<Navigate to="/" />} />
                          </Routes>
                        )}
                      </Router>
                    </BreadcrumbProvider>
                    {currentUser && (
                      <InactivityWarning
                        timeout={inactivityTimeout * 60}
                        idleThreshold={idleThreshold * 60}
                        onTimeout={updateInactivityTimeout}
                      />
                    )}
                  </LocalizationProvider>
                </CacheContext.Provider>
              </CalculatorProvider>
            </CalendarProvider>
          </ClockProvider>
        </ConnectivityProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ConnectivityProvider>
        <ClockProvider>
          <CalendarProvider>
            <CalculatorProvider>
              <CacheContext.Provider value={cacheContextValue}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <BreadcrumbProvider>
                    <AppContent />
                  </BreadcrumbProvider>
                </LocalizationProvider>
              </CacheContext.Provider>
            </CalculatorProvider>
          </CalendarProvider>
        </ClockProvider>
      </ConnectivityProvider>
    </AuthProvider>
  );
}

export default App;
